/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2022 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import * as Sentry from "@sentry/react";
import {
  SiteMetadata,
  GraphFavoriteData,
  OtherGraphFavoriteData,
  SiteEnergyData,
  ManufacturerData,
  BessCyclesAnalytics,
  AssemblyDetails,
  BessEmissionsAnalytics,
  WeatherResponse,
  WeatherForecastResponse,
  GensetRunHoursAnalytics,
  AlarmAction,
  InventoryItem,
  PagerDutyLiveService,
  PagerDutyCanopyIntegration,
  SitePagerDutyServices,
  TariffsResponse,
  TouIntervalsResponse,
  LsePropertiesResponse,
  BillConfigRequest,
  BillConfigResponse,
  BillConfigCreateResponse,
  BillCalculationRequest,
  BillCalculationResponse,
  BillCalculationBulkResponse,
  PerformanceReportResponse,
  CalculationStatusResponse,
  AssignMonthRequest,
  AssignMonthResponse,
  AssignedBillSummary,
  CalendarsResponse,
  CalendarDetailResponse,
  CalendarDatesResponse,
  SiteIntegrationResponse,
  ActivityGraphConfigData,
  ActivityGraphConfigPayload,
  UnifiedAlarmsResponse,
} from "./Types";

const basePresentationUrl = process.env.REACT_APP_SMS_NOC_PRESENTATION;
// Analytics and billing are routes on the presentation service, not separate
// deployments; their routers keep the /analytics and /billing prefixes.
const baseAnalyticsUrl = `${basePresentationUrl}/analytics`;
const baseBillingUrl = `${basePresentationUrl}/billing`;

/*
NTM - Create an internal instance.  This instance has the interceptors
attached to handle things like 401 and 403.  We do this so we can create
another instance without the interceptors attached for certain cases.
*/
const authenticatedInstance = axios.create();

/** Don't logout for endpoints related to user login system */
const shoudlIgnoreLogout = (url: string): boolean => {
  return url.includes("user");
};

const reportBackendServerError = (response: AxiosResponse): void => {
  // Only server faults are actionable; 4xx (401/403 in particular) are expected.
  if (response.status < 500 || response.status >= 600) {
    return;
  }

  const method = response.config.method?.toUpperCase() ?? "UNKNOWN";
  let url = "unknown";
  try {
    const parsedUrl = new URL(response.config.url ?? "", window.location.origin);
    url = `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    // Keep malformed URLs out of Sentry rather than risking query-string secrets.
  }

  Sentry.captureException(new Error(`Request to ${url} failed with ${response.status}`), {
    tags: {
      "error.source": "backend_response",
      "http.request.method": method,
      "http.status_code": response.status,
    },
    contexts: {
      response: {
        method,
        status_code: response.status,
        url,
      },
    },
    fingerprint: ["backend-error", method, String(response.status), url],
  });
};

const registerResponseInterceptor = (authLogout: Function): void => {
  const responseErrorInterceptor = (error: AxiosError): Promise<void> => {
    if (error.response) {
      reportBackendServerError(error.response);
    }

    // check the request error code. If it is an auth problem, redirect to the login page.
    if (
      !shoudlIgnoreLogout(error.config!.url!) &&
      (error.response?.status === 401 || error.response?.status === 403)
    ) {
      // if we can't get a response here, then try to call the
      // user info API.  Did the user accidentally hit something they shouldn't have?
      // If we can't validate the user then ... ?
      authLogout({ logoutParams: { returnTo: window.location.origin } });
    }
    return Promise.reject(error);
  };

  authenticatedInstance?.interceptors.response.use((res) => res, responseErrorInterceptor);
};

const isScaleDomain = (url: string): boolean => {
  // The analytics and billing URLs are prefixed by the presentation one.
  return url.includes(basePresentationUrl!);
};

const registerRequestInterceptor = (getAccessTokenSilently: Function): void => {
  const requestAuthInterceptor = async (
    config: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> => {
    if (!isScaleDomain(config.url!)) {
      return config;
    }

    try {
      const credential = await getAccessTokenSilently();

      config.headers = {
        Authorization: "Bearer " + credential,
        "content-type": "application/json",
      };
    } catch (error) {
      // request for credential failed so continue without one
    }
    // return scale domain request with or without credential
    return config;
  };

  // @ts-ignore
  authenticatedInstance.interceptors.request.use(requestAuthInterceptor, (req) => req);
};

const getPortfolioPower = (): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/portfolio/power/production`)
    .then((res: AxiosResponse) => res.data);
};

const getSiteTimeSeries = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
): Promise<any> => {
  const params = {
    interval: interval,
    start_time: start,
    stop_time: stop,
  };
  return (
    authenticatedInstance
      .get(`${basePresentationUrl}/site/${siteUuid}/time-series`, { params })
      // Soft miss: empty series keeps SitePowerGraph chrome (null unmounts it).
      .then((res: AxiosResponse) => (res.status === 204 ? { time_series: [] } : res.data))
  );
};

const getSitePowerCapacity = (siteUuid: string): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/power/capacity`)
    .then((res: AxiosResponse) => (res.status === 204 ? null : res.data));
};

const getSiteEnergyProduction = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
): Promise<SiteEnergyData | null> => {
  const params = {
    interval: interval,
    start_time: start,
    stop_time: stop,
  };
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/energy/production`, { params })
    .then((res: AxiosResponse) => (res.status === 204 ? null : res.data));
};

const getSiteAssemblies = (siteUuid: string, interval: string): Promise<any> => {
  const params = { interval: interval };
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/assemblies`, { params })
    .then((res: AxiosResponse) => (res.status === 204 ? null : res.data));
};

const getSiteMetaDataAssemblies = (siteUuid: string): Promise<AssemblyDetails[]> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/assets`)
    .then((res: AxiosResponse) => res.data);
};

export const getUserProfile = (): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/user`)
    .then((res: AxiosResponse) => res.data);
};

const getAlarmDetail = (siteUuid: string, alarmUuid: string): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/alarm/${alarmUuid}`)
    .then((res: AxiosResponse) => res.data);
};

// Get list of alarm alert strings for a site
const getSiteAlerts = async (siteUuid: string, assembliesUuid: string[]): Promise<string[]> => {
  const params = { assemblies_uuid: assembliesUuid.join(",") } as any;
  return authenticatedInstance
    .get(`${basePresentationUrl}/acct_mgmt/site/${siteUuid}/alerts`, { params })
    .then((res: AxiosResponse) => res.data);
};

const getSiteMetadata = (siteUuid: string): Promise<SiteMetadata> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}`)
    .then((res: AxiosResponse) => res.data);
};

export const getAlarmHistory = (siteUuid: string, start: string, stop: string): Promise<any> => {
  const params = {
    start_time: start,
    stop_time: stop,
  };
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/alarms/history`, { params })
    .then((res: AxiosResponse) => (res.status === 204 ? null : res.data));
};

const getTimeSeriesMetrics = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
  metricsUuids: string[],
): Promise<any> => {
  const params = {
    start_time: start,
    stop_time: stop,
    interval: interval,
    metrics: metricsUuids.toString(),
  };
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/time-series/metrics`, { params })
    .then((res: AxiosResponse) => res.data);
};

const getIsoList = (): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/iso/list`)
    .then((res: AxiosResponse) => res.data);
};

const getGasUtilitiesList = (): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/gas_utilities/list`)
    .then((res: AxiosResponse) => res.data);
};

const getManufacturersList = (scaleosType: string | null): Promise<ManufacturerData[]> => {
  const params = {
    scaleos_type: scaleosType,
  };
  return authenticatedInstance
    .get(`${basePresentationUrl}/manufacturer`, { params })
    .then((res: AxiosResponse) => res.data);
};

const getUtilityList = (filter: string): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/utilities/list?filter=${filter}`)
    .then((res: AxiosResponse) => res.data);
};

const getResourceList = (entity: string, scope: string): Promise<any> => {
  const params: { [key: string]: string } = {};
  if (entity !== "all") params[scope] = entity;
  return authenticatedInstance
    .get(`${basePresentationUrl}/resources/list`, { params })
    .then((res: AxiosResponse) => res.data);
};

// Inventory
const getInventory = async (): Promise<InventoryItem[]> => {
  const res: AxiosResponse<InventoryItem[]> = await authenticatedInstance.get(
    `${basePresentationUrl}/inventory`,
  );
  return res.data;
};

const getSiteList = async (): Promise<any> => {
  try {
    const response = await authenticatedInstance.get(`${basePresentationUrl}/sites/list`);
    return response.data;
  } catch (error) {
    throw new Error("Problem getting site list.");
  }
};

const getOrganizationsList = async (): Promise<any> => {
  try {
    const response = await authenticatedInstance.get(
      `${basePresentationUrl}/acct_mgmt/customer/list`,
    );
    return response.data;
  } catch (error) {
    throw new Error("Problem getting organizations list.");
  }
};

const getOrganizationSiteList = async (organizationUuid: string): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/acct_mgmt/organization/sites/list/${organizationUuid}`)
    .then((res: AxiosResponse) => res.data);
};

const getCurrentAlarms = async (siteUuid: string): Promise<any> => {
  return authenticatedInstance
    .get(`${process.env.REACT_APP_SMS_NOC_PRESENTATION}/site/${siteUuid}/alarm`)
    .then((res: AxiosResponse) => res.data);
};

const getUnifiedAlarms = (): Promise<UnifiedAlarmsResponse> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/alarms`)
    .then((res: AxiosResponse) => res.data);
};

// Fetch alarm actions configured for a site
const getSiteAlarmActions = async (siteUuid: string): Promise<AlarmAction[]> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/acct_mgmt/action/${siteUuid}`)
    .then((res: AxiosResponse) => res.data);
};

// Create a new alarm action
const postAlarmAction = async (body: {
  site_uuid: string;
  action_name: string;
  action_task: string;
  assemblies_uuids: string[];
  alerts?: string[];
  action_config?: Record<string, any>;
}): Promise<any> => {
  return authenticatedInstance
    .post(`${basePresentationUrl}/acct_mgmt/action`, body)
    .then((res: AxiosResponse) => res.data);
};

// Update an existing alarm action (replace)
const putAlarmAction = async (
  actionUuid: string,
  body: {
    site_uuid: string;
    action_name: string;
    action_task: string;
    assemblies_uuids: string[];
    alerts?: string[];
    action_config?: Record<string, any>;
  },
): Promise<any> => {
  return authenticatedInstance
    .put(`${basePresentationUrl}/acct_mgmt/action/${actionUuid}`, body)
    .then((res: AxiosResponse) => res.data);
};

// Delete an existing alarm action
const deleteAlarmAction = async (actionUuid: string): Promise<any> => {
  return authenticatedInstance
    .delete(`${basePresentationUrl}/acct_mgmt/action/${actionUuid}`)
    .then((res: AxiosResponse) => res.data);
};

const getOrganizationUsersList = async (organizationUuid: string): Promise<any> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/acct_mgmt/customer/${organizationUuid}/users`)
    .then((res: AxiosResponse) => res.data);
};

const getUsersByCustomers = async (customerUuids: string[]): Promise<any> => {
  if (customerUuids.length === 0) {
    return { users_by_customer: {} };
  }
  return authenticatedInstance
    .get(`${basePresentationUrl}/acct_mgmt/customers/users`, {
      params: { customer_uuids: customerUuids.join(",") },
    })
    .then((res: AxiosResponse) => res.data);
};

const createGraphFavorite = async (graph: GraphFavoriteData): Promise<any> => {
  return authenticatedInstance
    .post(`${basePresentationUrl}/graph`, graph)
    .then((res: AxiosResponse) => res.data);
};

const editGraphFavorite = async (graph: GraphFavoriteData): Promise<any> => {
  return authenticatedInstance
    .patch(`${basePresentationUrl}/graph`, graph)
    .then((res: AxiosResponse) => res.data);
};

const deleteGraphFavorite = async (graphUuid: string): Promise<any> => {
  return authenticatedInstance
    .delete(`${basePresentationUrl}/graph/${graphUuid}`)
    .then((res: AxiosResponse) => res.data);
};

const getActivityGraphConfig = async (siteUuid: string): Promise<ActivityGraphConfigData> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/site/${siteUuid}/activity_graph`)
    .then((res: AxiosResponse) => res.data);
};

const getActivityGraphConfigQuery = (siteUuid: string) => ({
  queryKey: ["activityGraphConfig", siteUuid],
  queryFn: () => getActivityGraphConfig(siteUuid),
  retry: false,
});

const putActivityGraphConfig = async (
  siteUuid: string,
  payload: ActivityGraphConfigPayload,
): Promise<ActivityGraphConfigData> => {
  return authenticatedInstance
    .put(`${basePresentationUrl}/acct_mgmt/site/${siteUuid}/activity_graph`, payload)
    .then((res: AxiosResponse) => res.data);
};

// PagerDuty endpoints
export const getPagerDutyServices = async (): Promise<PagerDutyLiveService[]> => {
  const res: AxiosResponse<PagerDutyLiveService[]> = await authenticatedInstance.get(
    `${basePresentationUrl}/acct_mgmt/pagerduty_services`,
  );
  return res.data;
};

export const getPagerDutyCanopyIntegrations = async (): Promise<PagerDutyCanopyIntegration[]> => {
  const res: AxiosResponse<PagerDutyCanopyIntegration[]> = await authenticatedInstance.get(
    `${basePresentationUrl}/acct_mgmt/pagerduty_canopy_integrations`,
  );
  return res.data;
};

export const postPagerDutyIntegration = async (
  id: string,
  body: { name: string; integration_key: string },
): Promise<any> => {
  const res: AxiosResponse = await authenticatedInstance.post(
    `${basePresentationUrl}/acct_mgmt/create_pagerduty_integration/${id}`,
    body,
  );
  return res.data;
};

export const getSitePagerDutyServices = async (
  siteUuid: string,
): Promise<SitePagerDutyServices> => {
  const res: AxiosResponse<SitePagerDutyServices> = await authenticatedInstance.get(
    `${basePresentationUrl}/acct_mgmt/site/${siteUuid}/pagerduty_services`,
  );
  return res.data;
};

export const getSitePagerDutyServicesQuery = (siteUuid: string) => ({
  queryKey: ["site-pd-services", siteUuid],
  queryFn: () => getSitePagerDutyServices(siteUuid),
});

export const putSitePagerDutyServices = async (
  siteUuid: string,
  serviceIds: string[],
): Promise<SitePagerDutyServices> => {
  const res: AxiosResponse<SitePagerDutyServices> = await authenticatedInstance.put(
    `${basePresentationUrl}/acct_mgmt/site/${siteUuid}/pagerduty_services`,
    { pagerduty_service_ids: serviceIds },
  );
  return res.data;
};

const getGraphFavorites = async (): Promise<GraphFavoriteData[]> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/graph`)
    .then((res: AxiosResponse) => res.data);
};

const getGraphFavoritesQuery = () => {
  return {
    queryKey: ["graph-favorites-"],
    queryFn: () => getGraphFavorites(),
    refetchOnMount: "always" as const,
    cacheTime: 0,
  };
};

const getOtherGraphFavorites = async (siteUuid: string): Promise<OtherGraphFavoriteData[]> => {
  return authenticatedInstance
    .get(`${basePresentationUrl}/graph/others`, { params: { site_uuid: siteUuid } })
    .then((res: AxiosResponse) => res.data);
};

const getOtherGraphFavoritesQuery = (siteUuid: string) => {
  return {
    queryKey: ["other-graph-favorites-" + siteUuid],
    queryFn: () => getOtherGraphFavorites(siteUuid),
    refetchOnMount: "always" as const,
    cacheTime: 0,
  };
};

const getPortfolioPowerQuery = () => {
  return {
    queryKey: ["portfolio-power"],
    queryFn: () => getPortfolioPower(),
    refetchInterval: 120000,
  };
};

const getSiteTimeSeriesQuery = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
) => {
  return {
    queryKey: ["site-time-series-" + siteUuid],
    queryFn: () => getSiteTimeSeries(siteUuid, start, stop, interval),
    // Refetch the data every 15 seconds
    refetchInterval: 15000,
  };
};

const getSitePowerCapacityQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-power-capacity-" + siteUuid],
    queryFn: () => getSitePowerCapacity(siteUuid),
    refetchInterval: 15000,
  };
};

const getSiteEnergyProductionQuery = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
) => {
  return {
    queryKey: ["site-energy-production-", siteUuid, start, stop, interval],
    queryFn: () => getSiteEnergyProduction(siteUuid, start, stop, interval),
    refetchInterval: 15000,
  };
};

const getSiteAssembliesQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-assemblies-" + siteUuid],
    queryFn: () => getSiteAssemblies(siteUuid, "120"),
  };
};

const getSiteMetaDataAssembliesQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-assemblies-metadata" + siteUuid],
    queryFn: () => getSiteMetaDataAssemblies(siteUuid),
  };
};

const getUserProfileQuery = () => {
  return {
    queryKey: ["user-profile"],
    queryFn: () => getUserProfile(),
  };
};

const getUserProfileMutation = () => {
  return {
    mutationKey: ["user-profile"],
    mutationFn: (userProfile: any) => {
      return authenticatedInstance.put(`${basePresentationUrl}/user`, userProfile);
    },
  };
};

const getSiteSettingsMutation = () => {
  return {
    mutationKey: ["site-settings"],
    mutationFn: (siteSettings: any) => {
      return authenticatedInstance.put(
        `${basePresentationUrl}/acct_mgmt/site/${siteSettings.uuid}`,
        siteSettings,
      );
    },
  };
};

const putUserSettingsMutation = () => {
  return {
    mutationKey: ["user-settings"],
    mutationFn: (userSettings: any) => {
      return authenticatedInstance.put(
        `${basePresentationUrl}/acct_mgmt/user/update`,
        userSettings,
      );
    },
  };
};

const putOwnUserSettingsMutation = () => {
  return {
    mutationKey: ["user-own-settings"],
    mutationFn: (userSettings: any) => {
      return authenticatedInstance.put(`${basePresentationUrl}/user`, userSettings);
    },
  };
};

const putOrganizationSettingsMutation = () => {
  return {
    mutationKey: ["organization-settings"],
    mutationFn: (organizationSettings: any) => {
      return authenticatedInstance.put(
        `${basePresentationUrl}/acct_mgmt/organization/${organizationSettings.uuid}`,
        organizationSettings,
      );
    },
  };
};

const putAssetMetadataMutation = () => {
  return {
    mutationKey: ["asset-metadata"],
    mutationFn: (asset: any) => {
      return authenticatedInstance.put(
        `${basePresentationUrl}/acct_mgmt/site/${asset.siteUuid}/assets/${asset.uuid}?site_name=${asset.siteName}&group_id=${asset.siteGroupId}`,
        asset,
      );
    },
  };
};

const postOrganizationSettingsMutation = () => {
  return {
    mutationKey: ["new-organization"],
    mutationFn: (organizationSettings: any) => {
      return authenticatedInstance.post(
        `${basePresentationUrl}/acct_mgmt/customer/add`,
        organizationSettings,
      );
    },
  };
};

const postSiteSettingsMutation = () => {
  return {
    mutationKey: ["new-site"],
    mutationFn: (siteSettings: any) => {
      return authenticatedInstance.post(`${basePresentationUrl}/acct_mgmt/site`, siteSettings);
    },
  };
};

const postUserSettingsMutation = () => {
  return {
    mutationKey: ["new-user"],
    mutationFn: (userSettings: any) => {
      return authenticatedInstance.post(`${basePresentationUrl}/acct_mgmt/user`, userSettings);
    },
  };
};

const postManufacturerMutation = () => {
  return {
    mutationKey: ["new-manufacturer"],
    mutationFn: (manufacturerData: ManufacturerData) => {
      return authenticatedInstance.post(
        `${basePresentationUrl}/acct_mgmt/manufacturer`,
        manufacturerData,
      );
    },
  };
};

const deleteOrganizationMutation = () => {
  return {
    mutationKey: ["organization-delete"],
    mutationFn: (organizationUuid: string) => {
      return authenticatedInstance.delete(
        `${basePresentationUrl}/acct_mgmt/customer/${organizationUuid}`,
      );
    },
  };
};

const deleteUserMutation = () => {
  return {
    mutationKey: ["user-delete"],
    mutationFn: (userEmail: string) => {
      return authenticatedInstance.delete(`${basePresentationUrl}/acct_mgmt/user/${userEmail}`);
    },
  };
};

const getAlarmDetailQuery = (siteUuid: string, alarmUuid: string) => {
  return {
    queryKey: ["alarm-detail-" + siteUuid + "-" + alarmUuid],
    queryFn: () => getAlarmDetail(siteUuid, alarmUuid),
  };
};

const getSiteMetadataQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-metadata-" + siteUuid],
    queryFn: () => getSiteMetadata(siteUuid),
  };
};

const getAlarmHistoryQuery = (siteUuid: string, start: string, stop: string) => {
  return {
    queryKey: ["alarm-history-" + siteUuid],
    queryFn: () => getAlarmHistory(siteUuid, start, stop),
    refetchInterval: 15000,
  };
};

const getTimeSeriesMetricsQuery = (
  siteUuid: string,
  start: string,
  stop: string,
  interval: string,
  metricsUuids: string[],
) => {
  const queryKey = [
    "time-series-metrics",
    siteUuid,
    start,
    stop,
    interval,
    metricsUuids.toString(),
  ];
  return {
    queryKey: queryKey,
    queryFn: () => getTimeSeriesMetrics(siteUuid, start, stop, interval, metricsUuids),
  };
};

const getIsoListQuery = () => {
  return {
    queryKey: ["iso-list"],
    queryFn: () => getIsoList(),
  };
};

const getGasUtilitiesListQuery = () => {
  return {
    queryKey: ["gas-list"],
    queryFn: () => getGasUtilitiesList(),
  };
};

const getManufacturersListQuery = (scaleosType: string | null) => {
  const queryKey = ["manufacturers-list", scaleosType];
  return {
    queryFn: () => getManufacturersList(scaleosType),
  };
};

const getUtilityListQuery = (filter: string) => {
  return {
    queryKey: ["utility"],
    queryFn: () => getUtilityList(filter),
  };
};

const getResourceListQuery = (entity: string, scope: string) => {
  return {
    queryKey: ["resource-" + scope + "-" + entity],
    queryFn: () => getResourceList(entity, scope),
  };
};

const getInventoryQuery = () => {
  return {
    queryKey: ["inventory"],
    queryFn: () => getInventory(),
    refetchOnMount: "always" as const,
    cacheTime: 0,
  };
};

const getSiteListQuery = () => {
  return {
    queryKey: ["site-list"],
    queryFn: () => getSiteList(),
  };
};

const getOrganizationsListQuery = () => {
  return {
    queryKey: ["organizations-list"],
    queryFn: () => getOrganizationsList(),
  };
};

const getOrganizationSitesListQuery = (organizationUuid: string) => {
  return {
    queryKey: ["organization-sites-list-" + organizationUuid],
    queryFn: () => getOrganizationSiteList(organizationUuid),
    enabled: Boolean(organizationUuid),
  };
};

const getCurrentAlarmsQuery = (siteUuid: string) => {
  return {
    queryKey: ["current-alarms-" + siteUuid],
    queryFn: () => getCurrentAlarms(siteUuid),
  };
};

const getUnifiedAlarmsQuery = () => {
  return {
    queryKey: ["unified-alarms"],
    queryFn: getUnifiedAlarms,
  };
};

const getOrganizationUsersListQuery = (organizationUuid: string) => {
  return {
    queryKey: ["organization-users-" + organizationUuid],
    queryFn: () => getOrganizationUsersList(organizationUuid),
    enabled: Boolean(organizationUuid),
  };
};

const getUsersByCustomersQuery = (customerUuids: string[]) => {
  return {
    queryKey: ["users-by-customers", customerUuids.sort().join(",")],
    queryFn: () => getUsersByCustomers(customerUuids),
  };
};

const getBessCyclesAnalytics = async (siteUuid: string): Promise<BessCyclesAnalytics[] | null> => {
  const res: AxiosResponse = await authenticatedInstance.get(`${baseAnalyticsUrl}/bess/cycles`, {
    params: { site_uuid: siteUuid },
  });

  if (res.status === 204) {
    return null;
  }

  // Reverse the data to show most recent first
  return res.data.reverse();
};

const getBessEmissionsAnalytics = async (
  siteUuid: string,
): Promise<BessEmissionsAnalytics[] | null> => {
  const res: AxiosResponse = await authenticatedInstance.get(`${baseAnalyticsUrl}/bess/emissions`, {
    params: { site_uuid: siteUuid },
  });
  if (res.status === 204) {
    return null;
  }

  // Reverse the data to show most recent first
  return res.data.reverse();
};

const getBessCyclesAnalyticsQuery = (siteUuid: string) => {
  return {
    queryKey: ["bess-cycles-analytics", siteUuid],
    queryFn: () => getBessCyclesAnalytics(siteUuid),
  };
};

const getBessEmissionsAnalyticsQuery = (siteUuid: string) => {
  return {
    queryKey: ["bess-emissions-analytics", siteUuid],
    queryFn: () => getBessEmissionsAnalytics(siteUuid),
  };
};

const getGensetRunHoursAnalytics = async (
  siteUuid: string,
  gensetUuid?: string,
): Promise<GensetRunHoursAnalytics | null> => {
  const params: any = { site_uuid: siteUuid };
  if (gensetUuid) params.genset_uuid = gensetUuid;
  const response = await authenticatedInstance.get(`${baseAnalyticsUrl}/genset/run_hours`, {
    params,
  });
  return response.data;
};

const getGensetRunHoursAnalyticsQuery = (siteUuid: string, gensetUuid?: string) => {
  return {
    queryKey: ["genset-run-hours-analytics", siteUuid, gensetUuid],
    queryFn: () => getGensetRunHoursAnalytics(siteUuid, gensetUuid),
  };
};

const getCurrentWeather = async (siteUuid: string): Promise<WeatherResponse> => {
  const response = await authenticatedInstance.get(
    `${basePresentationUrl}/site/${siteUuid}/weather/current`,
  );
  return response.data;
};

const getWeatherForecast = async (siteUuid: string): Promise<WeatherForecastResponse> => {
  const response = await authenticatedInstance.get(
    `${basePresentationUrl}/site/${siteUuid}/weather/forecast`,
  );
  return response.data;
};

const getCurrentWeatherQuery = (siteUuid: string) => {
  return {
    queryKey: ["current-weather", siteUuid],
    queryFn: () => getCurrentWeather(siteUuid),
    refetchInterval: 300000, // Refetch every 5 minutes
  };
};

const getWeatherForecastQuery = (siteUuid: string) => {
  return {
    queryKey: ["weather-forecast", siteUuid],
    queryFn: () => getWeatherForecast(siteUuid),
    refetchInterval: 3600000, // Refetch every hour
  };
};

const formatToYYYYMMDD = (dateStr: string): string => {
  return dateStr.split("T")[0];
};

/**
 * Get historical weather data for a site.
 * Time format matches other time series endpoints (ISO 8601 with timezone offset)
 * @param siteUuid - UUID of the site
 * @param start - Start time in ISO 8601 format (e.g. "2025-05-23T00:00:00-04:00")
 * @param stop - Stop time in ISO 8601 format (e.g. "2025-05-23T00:00:00-04:00")
 */
const getWeatherHistorical = async (
  siteUuid: string,
  start: string,
  stop: string,
): Promise<WeatherForecastResponse> => {
  const params = {
    start_date: formatToYYYYMMDD(start),
    end_date: formatToYYYYMMDD(stop),
  };

  let response = await authenticatedInstance.get(
    `${basePresentationUrl}/site/${siteUuid}/weather/historical`,
    { params },
  );

  return response.data;
};

const getWeatherHistoricalQuery = (siteUuid: string, start: string, stop: string) => {
  return {
    queryKey: ["weatherHistorical", siteUuid, start, stop],
    queryFn: () => getWeatherHistorical(siteUuid, start, stop),
    refetchInterval: 300000, // Refetch every 5 minutes
  };
};

// Query helper for alarm actions
const getSiteAlarmActionsQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-alarm-actions", siteUuid],
    queryFn: () => getSiteAlarmActions(siteUuid),
  };
};

// Query helper for site alerts (list of strings)
const getSiteAlertsQuery = (siteUuid: string, assembliesUuid: string[]) => {
  return {
    queryKey: ["site-alerts", siteUuid, ...(assembliesUuid || [])],
    queryFn: () => getSiteAlerts(siteUuid, assembliesUuid || []),
  };
};

// Tariff and LSE Properties API functions (Arcadia/Genability via billing service)
const getTariffs = async (masterTariffId: string): Promise<TariffsResponse> => {
  const res: AxiosResponse<TariffsResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/tariffs/${encodeURIComponent(masterTariffId)}`,
  );
  return res.data;
};

const getLseProperties = async (lseId: string): Promise<LsePropertiesResponse> => {
  const res: AxiosResponse<LsePropertiesResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/lse/${encodeURIComponent(lseId)}/properties`,
  );
  return res.data;
};

const getTariffsProperties = async (masterTariffId: string): Promise<LsePropertiesResponse> => {
  const res: AxiosResponse<LsePropertiesResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/tariffs/${encodeURIComponent(masterTariffId)}/properties`,
  );
  return res.data;
};

const getTariffsQuery = (masterTariffId: string) => {
  return {
    queryKey: ["tariffs", masterTariffId],
    queryFn: () => getTariffs(masterTariffId),
    enabled: !!masterTariffId && masterTariffId.trim() !== "",
  };
};

const getLsePropertiesQuery = (lseId: string) => {
  return {
    queryKey: ["lse-properties", lseId],
    queryFn: () => getLseProperties(lseId),
    enabled: !!lseId && lseId.trim() !== "",
  };
};

const getTariffsPropertiesQuery = (masterTariffId: string) => {
  return {
    queryKey: ["tariffs-properties", masterTariffId],
    queryFn: () => getTariffsProperties(masterTariffId),
    enabled: !!masterTariffId && masterTariffId.trim() !== "",
  };
};

// Time-of-Use intervals API functions (Arcadia/Genability via billing service)
const getTouIntervals = async (
  siteUuid: string,
  fromDateTime: string,
  toDateTime: string,
): Promise<TouIntervalsResponse> => {
  const params = { fromDateTime, toDateTime };
  const res: AxiosResponse<TouIntervalsResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/tou-intervals`,
    { params },
  );
  return res.data;
};

const getTouIntervalsQuery = (siteUuid: string, fromDateTime: string, toDateTime: string) => ({
  queryKey: ["tou-intervals", siteUuid, fromDateTime, toDateTime],
  queryFn: () => getTouIntervals(siteUuid, fromDateTime, toDateTime),
  enabled: !!siteUuid && !!fromDateTime && !!toDateTime,
  retry: false,
});

// Bill Configuration API functions
const getBillConfigs = async (siteUuid: string): Promise<BillConfigResponse[]> => {
  const res: AxiosResponse<BillConfigResponse[]> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/bill-config`,
  );
  return res.data;
};

const getBillConfigById = async (
  siteUuid: string,
  configId: number,
): Promise<BillConfigResponse> => {
  const res: AxiosResponse<BillConfigResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/bill-config/${configId}`,
  );
  return res.data;
};

const createBillConfig = async (
  siteUuid: string,
  config: BillConfigRequest,
): Promise<BillConfigCreateResponse> => {
  const res: AxiosResponse<BillConfigCreateResponse> = await authenticatedInstance.post(
    `${baseBillingUrl}/site/${siteUuid}/bill-config`,
    config,
  );
  return res.data;
};

const updateBillConfig = async (
  siteUuid: string,
  configId: number,
  config: BillConfigRequest,
): Promise<BillConfigCreateResponse> => {
  const res: AxiosResponse<BillConfigCreateResponse> = await authenticatedInstance.put(
    `${baseBillingUrl}/site/${siteUuid}/bill-config/${configId}`,
    config,
  );
  return res.data;
};

const deleteBillConfig = async (siteUuid: string, configId: number): Promise<any> => {
  const res: AxiosResponse = await authenticatedInstance.delete(
    `${baseBillingUrl}/site/${siteUuid}/bill-config/${configId}`,
  );
  return res.data;
};

const getBillConfigsQuery = (siteUuid: string) => {
  return {
    queryKey: ["bill-configs", siteUuid],
    queryFn: () => getBillConfigs(siteUuid),
    enabled: !!siteUuid,
  };
};

const createBillConfigMutation = () => {
  return {
    mutationKey: ["create-bill-config"],
    mutationFn: ({ siteUuid, config }: { siteUuid: string; config: BillConfigRequest }) => {
      return createBillConfig(siteUuid, config);
    },
  };
};

const updateBillConfigMutation = () => {
  return {
    mutationKey: ["update-bill-config"],
    mutationFn: ({
      siteUuid,
      configId,
      config,
    }: {
      siteUuid: string;
      configId: number;
      config: BillConfigRequest;
    }) => {
      return updateBillConfig(siteUuid, configId, config);
    },
  };
};

const deleteBillConfigMutation = () => {
  return {
    mutationKey: ["delete-bill-config"],
    mutationFn: ({ siteUuid, configId }: { siteUuid: string; configId: number }) => {
      return deleteBillConfig(siteUuid, configId);
    },
  };
};

const calculateBill = async (
  request: BillCalculationRequest,
): Promise<BillCalculationBulkResponse> => {
  const res: AxiosResponse<BillCalculationBulkResponse> = await authenticatedInstance.post(
    `${baseBillingUrl}/bill/calculate-bill`,
    request,
  );
  return res.data;
};

// Performance Report API functions
const getPerformanceReport = async (
  siteUuid: string,
  billingCycleStart: string,
  billingCycleEnd: string,
): Promise<PerformanceReportResponse> => {
  const res: AxiosResponse<PerformanceReportResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/performance-report`,
    {
      params: {
        billing_cycle_start: billingCycleStart,
        billing_cycle_end: billingCycleEnd,
      },
    },
  );
  return res.data;
};

const getPerformanceReportQuery = (
  siteUuid: string | undefined,
  billingCycleStart: string | undefined,
  billingCycleEnd: string | undefined,
) => {
  return {
    queryKey: ["performanceReport", siteUuid, billingCycleStart, billingCycleEnd],
    queryFn: () => getPerformanceReport(siteUuid!, billingCycleStart!, billingCycleEnd!),
    enabled: !!siteUuid && !!billingCycleStart && !!billingCycleEnd,
  };
};

// Latest Performance Report (no date params needed)
const getLatestPerformanceReport = async (siteUuid: string): Promise<PerformanceReportResponse> => {
  const res: AxiosResponse<PerformanceReportResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/performance-report/latest`,
  );
  return res.data;
};

const getLatestPerformanceReportQuery = (siteUuid: string | undefined) => {
  return {
    queryKey: ["latestPerformanceReport", siteUuid],
    queryFn: () => getLatestPerformanceReport(siteUuid!),
    enabled: !!siteUuid,
    retry: (failureCount: number, error: AxiosError) => {
      // Don't retry on 404 (used as "no report exists" signal)
      if (error?.response?.status === 404) return false;
      // Retry up to 2 times for all other errors (5xx, network, etc.)
      return failureCount < 2;
    },
  };
};

// Calculation status polling
const getCalculationStatus = async (calculationId: number): Promise<CalculationStatusResponse> => {
  const res: AxiosResponse<CalculationStatusResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/bill/calculate/${calculationId}/status`,
  );
  return res.data;
};

const getCalculationStatusQuery = (calculationId: number | undefined, enabled: boolean = false) => {
  return {
    queryKey: ["calculationStatus", calculationId],
    queryFn: () => getCalculationStatus(calculationId!),
    enabled: !!calculationId && enabled,
    refetchInterval: enabled ? 3000 : false,
  };
};

// Fetch a CSV endpoint and hand it to the browser as a file download using the
// caller's filename (the UI names report files, not the backend).
const downloadCsv = async (
  url: string,
  filename: string,
  params?: Record<string, string>,
): Promise<void> => {
  const res = await authenticatedInstance.get(url, { params, responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

// Download a report's line items as CSV. Without a billing period the
// backend falls back to the site's latest one.
const downloadReportCsv = (
  siteUuid: string,
  filename: string,
  billingCycleStart?: string,
  billingCycleEnd?: string,
): Promise<void> =>
  downloadCsv(
    `${baseBillingUrl}/site/${siteUuid}/performance-report/latest/download`,
    filename,
    billingCycleStart && billingCycleEnd
      ? {
          billing_cycle_start: billingCycleStart,
          billing_cycle_end: billingCycleEnd,
        }
      : undefined,
  );

// Download the Fact Financials CSV for a specific billing period
const downloadFactFinancialsCsv = (
  siteUuid: string,
  billingCycleStart: string,
  billingCycleEnd: string,
  filename: string,
): Promise<void> =>
  downloadCsv(
    `${baseBillingUrl}/site/${siteUuid}/performance-report/fact-financials/download`,
    filename,
    {
      billing_cycle_start: billingCycleStart,
      billing_cycle_end: billingCycleEnd,
    },
  );

// Bill Calculation Pair API functions
const deleteBillCalculationPair = async (pairId: string): Promise<void> => {
  await authenticatedInstance.delete(`${baseBillingUrl}/bill/calculate-bill/${pairId}`);
};

const assignMonthToPair = async (
  pairId: string,
  request: AssignMonthRequest,
): Promise<AssignMonthResponse> => {
  const res: AxiosResponse<AssignMonthResponse> = await authenticatedInstance.patch(
    `${baseBillingUrl}/bill/calculate-bill/${pairId}/assign-month`,
    request,
  );
  return res.data;
};

const getAssignedCalculations = async (siteUuid: string): Promise<AssignedBillSummary[]> => {
  const res: AxiosResponse<AssignedBillSummary[]> = await authenticatedInstance.get(
    `${baseBillingUrl}/bill/calculate-bill/assigned/${siteUuid}`,
  );
  return res.data;
};

const getAssignedCalculationsQuery = (siteUuid: string | undefined) => {
  return {
    queryKey: ["assignedCalculations", siteUuid],
    queryFn: () => getAssignedCalculations(siteUuid!),
    enabled: !!siteUuid,
  };
};

// Arcadia Calendar API functions
const getArcadiaCalendars = async (lseId: number): Promise<CalendarsResponse> => {
  const res: AxiosResponse<CalendarsResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/arcadia/calendars`,
    { params: { lse_id: lseId } },
  );
  return res.data;
};

const getArcadiaCalendarsQuery = (lseId: number) => {
  return {
    queryKey: ["arcadia-calendars", lseId],
    queryFn: () => getArcadiaCalendars(lseId),
    enabled: !!lseId,
  };
};

const getArcadiaCalendarDetail = async (calendarId: number): Promise<CalendarDetailResponse> => {
  const res: AxiosResponse<CalendarDetailResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/arcadia/calendars/${calendarId}`,
  );
  return res.data;
};

const getArcadiaCalendarDetailQuery = (calendarId: number) => {
  return {
    queryKey: ["arcadia-calendar-detail", calendarId],
    queryFn: () => getArcadiaCalendarDetail(calendarId),
    enabled: !!calendarId,
  };
};

const getArcadiaCalendarDates = async (
  calendarId: number,
  fromDateTime: string,
  toDateTime: string,
): Promise<CalendarDatesResponse> => {
  const res: AxiosResponse<CalendarDatesResponse> = await authenticatedInstance.get(
    `${baseBillingUrl}/arcadia/calendars/dates`,
    { params: { calendar_id: calendarId, fromDateTime: fromDateTime, toDateTime: toDateTime } },
  );
  return res.data;
};

const getArcadiaCalendarDatesQuery = (
  calendarId: number,
  fromDateTime: string,
  toDateTime: string,
) => {
  return {
    queryKey: ["arcadia-calendar-dates", calendarId, fromDateTime, toDateTime],
    queryFn: () => getArcadiaCalendarDates(calendarId, fromDateTime, toDateTime),
    enabled: !!calendarId && !!fromDateTime && !!toDateTime,
  };
};

// Site Integration API functions
const getSiteIntegrations = async (siteUuid: string): Promise<SiteIntegrationResponse[]> => {
  const res: AxiosResponse<SiteIntegrationResponse[]> = await authenticatedInstance.get(
    `${baseBillingUrl}/site/${siteUuid}/integrations`,
  );
  return res.data;
};

const getSiteIntegrationsQuery = (siteUuid: string) => {
  return {
    queryKey: ["site-integrations", siteUuid],
    queryFn: () => getSiteIntegrations(siteUuid),
    enabled: !!siteUuid,
  };
};

const upsertSiteIntegration = async (
  siteUuid: string,
  provider: string,
  data: { config: Record<string, unknown>; data_types: string[]; active: boolean },
): Promise<SiteIntegrationResponse> => {
  const res: AxiosResponse<SiteIntegrationResponse> = await authenticatedInstance.put(
    `${baseBillingUrl}/site/${siteUuid}/integrations/${provider}`,
    data,
  );
  return res.data;
};

const deleteSiteIntegration = async (siteUuid: string, provider: string): Promise<void> => {
  await authenticatedInstance.delete(`${baseBillingUrl}/site/${siteUuid}/integrations/${provider}`);
};

const getUpkeepLocationName = async (locationId: string): Promise<{ name: string }> => {
  const res: AxiosResponse<{ name: string }> = await authenticatedInstance.get(
    `${basePresentationUrl}/acct_mgmt/upkeep/location/${locationId}`,
  );
  return res.data;
};

const getUpkeepLocationNameQuery = (locationId: string) => {
  return {
    queryKey: ["upkeep-location", locationId],
    queryFn: () => getUpkeepLocationName(locationId),
  };
};

export {
  getPortfolioPowerQuery,
  getSiteTimeSeriesQuery,
  getSitePowerCapacityQuery,
  getSitePowerCapacity,
  getSiteEnergyProductionQuery,
  getSiteAssembliesQuery,
  getSiteMetaDataAssembliesQuery,
  getUserProfileQuery,
  getUserProfileMutation,
  getSiteSettingsMutation,
  getAlarmDetailQuery,
  getSiteMetadataQuery,
  getAlarmHistoryQuery,
  getCurrentAlarmsQuery,
  getCurrentAlarms,
  getUnifiedAlarmsQuery,
  getTimeSeriesMetricsQuery,
  getIsoListQuery,
  getGasUtilitiesListQuery,
  getManufacturersListQuery,
  getUtilityListQuery,
  getResourceListQuery,
  getInventory,
  getInventoryQuery,
  getSiteTimeSeries,
  getTimeSeriesMetrics,
  getSiteList,
  getSiteListQuery,
  getOrganizationsListQuery,
  putOrganizationSettingsMutation,
  putAssetMetadataMutation,
  calculateBill,
  postOrganizationSettingsMutation,
  deleteOrganizationMutation,
  getOrganizationSitesListQuery,
  postSiteSettingsMutation,
  getOrganizationUsersListQuery,
  getUsersByCustomersQuery,
  postUserSettingsMutation,
  postManufacturerMutation,
  deleteUserMutation,
  putUserSettingsMutation,
  putOwnUserSettingsMutation,
  registerRequestInterceptor,
  registerResponseInterceptor,
  reportBackendServerError,
  createGraphFavorite,
  editGraphFavorite,
  getGraphFavoritesQuery,
  getOtherGraphFavorites,
  getOtherGraphFavoritesQuery,
  deleteGraphFavorite,
  getBessCyclesAnalytics,
  getBessCyclesAnalyticsQuery,
  getBessEmissionsAnalytics,
  getBessEmissionsAnalyticsQuery,
  getGensetRunHoursAnalytics,
  getGensetRunHoursAnalyticsQuery,
  getCurrentWeather,
  getWeatherForecast,
  getCurrentWeatherQuery,
  getWeatherForecastQuery,
  getWeatherHistorical,
  getWeatherHistoricalQuery,
  getSiteAlarmActions,
  getSiteAlarmActionsQuery,
  getSiteAlerts,
  getSiteAlertsQuery,
  postAlarmAction,
  putAlarmAction,
  deleteAlarmAction,
  getTariffs,
  getTariffsQuery,
  getLseProperties,
  getLsePropertiesQuery,
  getTariffsProperties,
  getTariffsPropertiesQuery,
  getTouIntervals,
  getTouIntervalsQuery,
  getBillConfigs,
  getBillConfigById,
  getBillConfigsQuery,
  createBillConfig,
  createBillConfigMutation,
  updateBillConfig,
  updateBillConfigMutation,
  deleteBillConfig,
  deleteBillConfigMutation,
  getPerformanceReport,
  getPerformanceReportQuery,
  getLatestPerformanceReport,
  getLatestPerformanceReportQuery,
  getCalculationStatus,
  getCalculationStatusQuery,
  downloadReportCsv,
  downloadFactFinancialsCsv,
  deleteBillCalculationPair,
  assignMonthToPair,
  getAssignedCalculations,
  getAssignedCalculationsQuery,
  getArcadiaCalendars,
  getArcadiaCalendarsQuery,
  getArcadiaCalendarDetail,
  getArcadiaCalendarDetailQuery,
  getArcadiaCalendarDates,
  getArcadiaCalendarDatesQuery,
  getSiteIntegrations,
  getSiteIntegrationsQuery,
  upsertSiteIntegration,
  deleteSiteIntegration,
  getUpkeepLocationName,
  getUpkeepLocationNameQuery,
  getActivityGraphConfig,
  getActivityGraphConfigQuery,
  putActivityGraphConfig,
};
